import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  getSubmissionSynopsisPortal,
  patchSubmissionPortal,
  postSubmissionSynopsisVerifiedPortal,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";

import { detailsDifferFromSaved } from "@/components/online-reporting/report-view/utils";

import type { NotesSidePanelProps } from "./NotesSidePanel.types";

export function useNotesSidePanel(props: NotesSidePanelProps) {
  const {
    isOpen,
    onClose,
    getCurrentValuesRef,
    reportMetadata,
    setReportMetadata,
    isClosed,
    modified,
    setModified,
    updateIncidentNumberAndNotes,
    guardIncidentNumber,
    supplementalDrafts = {},
    onSupplementalDraftChange,
    onSupplementalUpdate,
    onCurrentReportChange,
    externalReportIndex,
    requireSynopsisVerification = false,
    synopsisVerified = false,
    setSynopsisVerified,
    onWidthChange,
    initialWidth,
  } = props;

  const [notes, setNotes] = useState(reportMetadata?.notes ?? "");
  const [synopsis, setSynopsis] = useState(reportMetadata?.synopsis ?? "");
  const [incidentNumber, setIncidentNumber] = useState(
    reportMetadata?.incident_number ?? "",
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingSynopsis, setIsGeneratingSynopsis] = useState(false);
  const [synopsisVerifiedLoading, setSynopsisVerifiedLoading] = useState(false);
  const [panelWidth, setPanelWidth] = useState(initialWidth ?? 500);
  const [currentReportIndex, setCurrentReportIndex] = useState(0);

  const allReports = useMemo(
    () => [
      reportMetadata,
      ...(reportMetadata?.supplementary_submissions || []),
    ],
    [reportMetadata],
  );
  const currentReport = allReports[currentReportIndex];
  const isCurrentReportMain = currentReportIndex === 0;
  const isCurrentReportSupplemental = !isCurrentReportMain;
  const isCurrentReportExported = !!currentReport?.last_export_time;
  const isMainReportExported = !!reportMetadata?.last_export_time;

  const currentSupplementalDraft = isCurrentReportMain
    ? undefined
    : supplementalDrafts[currentReport?.submission_id];

  const currentNotes = isCurrentReportMain
    ? notes
    : (currentSupplementalDraft?.notes ?? currentReport?.notes ?? "");

  const currentSynopsis = isCurrentReportMain
    ? synopsis
    : (currentSupplementalDraft?.synopsis ?? currentReport?.synopsis ?? "");

  /* Incident number is a property of the case (main record). Supplementals
  inherit it, so the side panel always shows and edits the main record's
  incident number regardless of the selected report tab. */
  const currentIncidentNumber = incidentNumber;

  const currentModified = isCurrentReportMain
    ? modified
    : !!currentSupplementalDraft;

  const currentNotesModified = currentNotes !== (currentReport?.notes ?? "");

  const hasSynopsisContent = !!currentSynopsis?.trim();
  const enforceSynopsisVerification =
    requireSynopsisVerification && hasSynopsisContent && isCurrentReportMain;

  /* Keeps `modified` accurate so undoing an edit (e.g. clearing the incident
  number via the duplicate dialog's Clear Number) drops the dirty state
  instead of leaving a false "unsaved changes" warning. Safe because the
  main-view form and this panel are mutually exclusive editing surfaces, so
  no hidden main-view field can be dirty while the panel is open. Priority
  and tags are main-view-only, so they're omitted (never dirty here). */
  const isMainDetailsDirty = (next: {
    notes?: string;
    synopsis?: string;
    incidentNumber?: string;
  }) =>
    detailsDifferFromSaved(
      {
        incidentNumber: next.incidentNumber ?? incidentNumber,
        notes: next.notes ?? notes,
        synopsis: next.synopsis ?? synopsis,
      },
      reportMetadata ?? {},
    );

  const setCurrentNotes = (value: string) => {
    if (isCurrentReportMain) {
      setNotes(value);
      setModified(isMainDetailsDirty({ notes: value }));
    } else {
      onSupplementalDraftChange?.(currentReport?.submission_id, {
        notes: value,
      });
    }
  };

  const setCurrentSynopsis = (value: string) => {
    if (isCurrentReportMain) {
      setSynopsis(value);
      setModified(isMainDetailsDirty({ synopsis: value }));
      setSynopsisVerified?.(false);
    } else {
      onSupplementalDraftChange?.(currentReport?.submission_id, {
        synopsis: value,
      });
    }
  };

  const setCurrentIncidentNumber = (value: string) => {
    setIncidentNumber(value);
    setModified(isMainDetailsDirty({ incidentNumber: value }));
  };

  if (getCurrentValuesRef) {
    getCurrentValuesRef.current = () => ({
      notes,
      synopsis,
      incident_number: incidentNumber,
    });
  }

  useEffect(() => {
    if (externalReportIndex !== undefined) {
      setCurrentReportIndex(externalReportIndex);
    }
  }, [externalReportIndex]);

  const prevIsOpenRef = useRef(false);
  const prevSubmissionIdRef = useRef(reportMetadata?.submission_id);

  /* Sync local state from reportMetadata when:
  1. The panel first opens (closed → open transition)
  2. The viewed submission changes (e.g. linking navigates to a different report)
  We intentionally skip re-syncing when reportMetadata updates for other reasons
  (e.g. Reviewer Details save) so unsaved edits in the panel aren't wiped. */
  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    const submissionChanged =
      reportMetadata?.submission_id !== prevSubmissionIdRef.current;

    if (justOpened || submissionChanged) {
      setNotes(reportMetadata?.notes ?? "");
      setSynopsis(reportMetadata?.synopsis ?? "");
      setIncidentNumber(reportMetadata?.incident_number ?? "");

      // When the submission changes, navigate back to the main report tab so we
      // don't show stale supplemental data
      if (submissionChanged) {
        setCurrentReportIndex(0);
        onCurrentReportChange?.(0);
      }
    }

    prevIsOpenRef.current = isOpen;
    prevSubmissionIdRef.current = reportMetadata?.submission_id;
  }, [isOpen, reportMetadata]);

  const handleClose = () => {
    setReportMetadata({
      ...reportMetadata,
      notes,
      synopsis,
      incident_number: incidentNumber,
    });
    onClose();
  };

  const navigateToReport = useCallback(
    (index: number) => {
      setCurrentReportIndex(index);
      onCurrentReportChange?.(index);
    },
    [onCurrentReportChange],
  );

  const generateSynopsis = async () => {
    setIsGeneratingSynopsis(true);
    try {
      const response = (
        await getSubmissionSynopsisPortal({
          path: { submission_id: currentReport?.submission_id || "" },
        })
      ).data!;
      setCurrentSynopsis(response.synopsis);
      if (isCurrentReportMain) {
        setSynopsisVerified?.(false);
      }
    } catch (error) {
      console.error("Error generating synopsis:", error);
    } finally {
      setIsGeneratingSynopsis(false);
    }
  };

  const handleSave = async () => {
    if (
      isCurrentReportMain &&
      enforceSynopsisVerification &&
      !synopsisVerified
    ) {
      toast.error(
        "Please confirm you have verified the synopsis before saving.",
      );
      return;
    }
    /* Advisory duplicate-incident-number check. The incident number belongs to
     the main record, so only guard when saving it. Aborts the save if the
     officer chooses to clear the number (or dismisses the warning). */
    if (isCurrentReportMain && guardIncidentNumber) {
      const proceed = await guardIncidentNumber(incidentNumber);
      if (!proceed) return;
    }
    setIsSaving(true);
    try {
      if (isCurrentReportMain) {
        setReportMetadata({
          ...reportMetadata,
          notes,
          synopsis,
          incident_number: incidentNumber,
        });
        await updateIncidentNumberAndNotes({
          incident_number: incidentNumber,
          notes,
          synopsis,
        });
      } else {
        await patchSubmissionPortal({
          path: { submission_id: currentReport?.submission_id },
          body: {
            notes: currentNotes,
            synopsis: currentSynopsis,
          },
        });
        onSupplementalUpdate?.(
          currentReport?.submission_id,
          currentNotes,
          currentSynopsis,
        );
      }
    } catch (error) {
      console.error("Error saving notes:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSynopsisVerificationChange = async (checked: boolean) => {
    if (!checked) {
      setSynopsisVerified?.(false);
      return;
    }
    setSynopsisVerifiedLoading(true);
    try {
      await postSubmissionSynopsisVerifiedPortal({
        path: { submission_id: reportMetadata?.submission_id ?? "" },
      });
      setSynopsisVerified?.(true);
    } catch {
      toast.error("Failed to record synopsis verification. Please try again.");
      setSynopsisVerified?.(false);
    } finally {
      setSynopsisVerifiedLoading(false);
    }
  };

  return {
    panelWidth,
    setPanelWidth,
    allReports,
    currentReport,
    currentReportIndex,
    isCurrentReportMain,
    isCurrentReportSupplemental,
    isCurrentReportExported,
    isMainReportExported,
    currentNotes,
    currentSynopsis,
    currentIncidentNumber,
    currentModified,
    currentNotesModified,
    setCurrentNotes,
    setCurrentSynopsis,
    setCurrentIncidentNumber,
    handleClose,
    navigateToReport,
    generateSynopsis,
    handleSave,
    isSaving,
    isGeneratingSynopsis,
    synopsisVerifiedLoading,
    enforceSynopsisVerification,
    reportMetadata,
    onWidthChange,
    handleSynopsisVerificationChange,
  };
}
